import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Download, FileArchive } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { Loader2 } from 'src/components/ui/animated-loader2';
import { useDownloadFiles, type UseDownloadFilesOptions } from './use-download-files';
import { cn } from 'src/lib/utils';

export interface DownloadFilesDropdownProps extends UseDownloadFilesOptions {
  /** Additional class names for the trigger button */
  className?: string;
  /** Button variant */
  variant?: 'outline' | 'default' | 'brand' | 'accent1';
  /** Button size */
  size?: 'sm' | 'iconStartSm' | 'responsive' | 'default';
  /** Dropdown alignment */
  align?: 'start' | 'center' | 'end';
}

export const DownloadFilesDropdown: React.FC<DownloadFilesDropdownProps> = ({
  getFiles,
  zipFileName,
  className,
  variant = 'outline',
  size = 'iconStartSm',
  align = 'end',
}) => {
  const { t } = useTranslation();
  const { isDownloading, downloadProgress, handleDownloadAsZip, handleDownloadOneByOne } =
    useDownloadFiles({ getFiles, zipFileName });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={cn('tw-gap-2', className)}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />
          ) : (
            <Download className="tw-h-4 tw-w-4" />
          )}
          {downloadProgress ? (
            <span className="tw-flex tw-flex-col tw-items-start tw-gap-0.5">
              <span className="tw-text-xs tw-tabular-nums">
                {downloadProgress.current}/{downloadProgress.total}
                {downloadProgress.percentage !== undefined && (
                  <span className="tw-ml-1">({downloadProgress.percentage}%)</span>
                )}
              </span>
              {downloadProgress.filename && (
                <span className="tw-max-w-[200px] tw-truncate tw-text-xs tw-text-muted-foreground">
                  {downloadProgress.filename}
                </span>
              )}
            </span>
          ) : (
            t('actions:download_all', { defaultValue: 'Download All' })
          )}
          <ChevronDown className="tw-h-4 tw-w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        <DropdownMenuItem onClick={handleDownloadAsZip} disabled={isDownloading}>
          <FileArchive className="tw-mr-2 tw-h-4 tw-w-4" />
          {t('actions:download_as_zip', { defaultValue: 'Download as ZIP' })}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleDownloadOneByOne} disabled={isDownloading}>
          <Download className="tw-mr-2 tw-h-4 tw-w-4" />
          {t('actions:download_one_by_one', { defaultValue: 'Download one by one' })}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
